import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/Reveal";
import { APPROACH_IMAGE } from "@/lib/content";

const PHASES = [
  {
    num: "Phase 01",
    icon: "/assets/icon-culture-assessment.png",
    title: "Assessment — the honest baseline",
    intro: "You cannot change an attitude you haven't measured. We start by listening.",
    body: "Through confidential interviews, frontline observation and attitude diagnostics, we map how risk is genuinely perceived across your organisation — from the boardroom to the night shift. We surface the normalised shortcuts, the unspoken fears and the gap between documented and lived safety culture.",
    outcomes: ["A true reflection of your culture and the risks that it enables", "The specific attitudes and organisational systems driving risk taking", "A baseline to evidence a significant shift in both attitude and behaviour", "Not a bland, predictable paper exercise but a thorough exploration through honest and open 1:1 discussions"],
  },
  {
    num: "Phase 02",
    icon: "/assets/icon-bespoke-workshops.png",
    title: "Consultant-actor led engagement — the emotional shift",
    intro: "People change when they feel something, not when they're told something.",
    body: "Our professional consultant-actors recreate your workplace's defining moments — the deadline pressure, the casual shortcut, the intervention that never happened. Delegates stop the action, direct the actors and rehearse different choices. It is visceral, specific to your world, and impossible to forget.",
    outcomes: ["Attitudes challenged AND changed in a single workshop session", "Lightbulb moments that teams reference for months", "Leaders seeing in front of them the impact of what they say and do has on others", "Critical requirements that have been overlooked or unappreciated suddenly become valuable"],
  },
  {
    num: "Phase 03",
    icon: "/assets/icon-performance-management.png",
    title: "Lasting behaviour change — the habit",
    intro: "A workshop is a spark. Habits are the fire that keeps burning.",
    body: "We reshape the processes around your people so the right behaviour is also the easy behaviour — simple, observable, repeatable. Performance critical habits, visible follow-up and just-culture accountability turn the emotional shift into a resilient culture that holds under financial pressure, client demands and human nature.",
    outcomes: ["Performance critical habits such as adherence to Life Saving Rules embedded in daily work", "Processes and systems that encourage the right behaviours and are fair", "Perfectly designed leading performance indicators based on behaviour and risk", "A culture that is resilient and delivers performance to be proud of"],
  },
];

export default function Approach() {
  return (
    <div data-testid="approach-page">
      <PageHero
        eyebrow="Our approach"
        title="Change that outlives the workshop"
        description="Awareness fades within weeks. Our three-phase approach is engineered so the change doesn't — assess honestly, engage emotionally, then systematically embed behaviours as habits."
        image={APPROACH_IMAGE}
        alt="Facilitator engaging a team in an interactive safety session"
      />

      <section className="bg-white py-20 lg:py-32">
        <div className="container-arms max-w-4xl">
          <div className="relative space-y-20 before:absolute before:left-[27px] before:top-2 before:h-[calc(100%-2rem)] before:w-px before:bg-teal/30 sm:before:left-[31px]" data-testid="approach-timeline">
            {PHASES.map((p, i) => {
              return (
                <Reveal key={p.num} delay={0.05}>
                  <article className="relative grid gap-6 pl-20 sm:pl-24" data-testid={`approach-phase-${i + 1}`}>
                    <span className="absolute left-0 top-0 flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-white shadow-sm sm:h-16 sm:w-16">
                      <img src={p.icon} alt="" loading="lazy" className="h-full w-full object-cover" />
                    </span>
                    <div>
                      <p className="eyebrow mb-2">{p.num}</p>
                      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-navy">{p.title}</h2>
                      <p className="mt-3 text-base sm:text-lg font-medium text-teal">{p.intro}</p>
                      <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">{p.body}</p>
                      <ul className="mt-6 grid gap-2 sm:grid-cols-1">
                        {p.outcomes.map((o) => (
                          <li key={o} className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-teal" />
                            {o}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CtaBand
        title="See the approach working in your world"
        text="Tell us about your organisation and your cultural challenges - we'll explain exactly how we could use one or all three of these phases to help you create a high performance culture."
      />
    </div>
  );
}
